import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from 'react-redux';
import { useNavigate } from "@/lib/router-dom";
import { userRole } from '@/config/constants';
import { ReactComponent as IconChevronDown } from "@/assets/svgs/chevron-down.svg";
import { ReactComponent as IconUploadButton } from "@/assets/svgs/upload-icon.svg";
import { ReactComponent as IconNoMessage } from "@/assets/svgs/no-message.svg";
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index.jsx";
import Search from "@/componentsDS/Search/index";
import api from "@/services/api/master-data-management.js";
import moment from "moment";
import rem from "@/utils/rem";
import { features as featureConstant } from "@/utils/feature-verifier";
import { useFeature } from "@/hooks/use-feature/index";
import { useBuildex } from "@/hooks/use-buildex";
import usePage from "@/hooks/use-page";
import useDataPage from "@/hooks/use-data-page/index";
import useMasterDataManagement from "@/hooks/use-master-data-management/index";
import getPageLabel from "@/hooks/use-page-details/index";
import useToast from "@/hooks/use-toast";
import MultiLevelDropDown from "@/components/MultiLevelMenuDropDown/index.js";
import Toast from "@/components/Toast/index";
import toastType from "@/constants/toast-type/index";

const MasterDataManagementPage = () => {
	/*=================================================1
		General
	/================================================*/
	const isBuildex = useBuildex();
	const isSupersetDashboard = useFeature(featureConstant.DASHBOARD);
	const isHomePage = !(isBuildex && isSupersetDashboard);
	const user = useSelector((state) => state.auth.profile);
	const isSuperAdmin = user?.role?.some((role) => role.name === userRole.SUPER_ADMIN);
	const [pageTitle] = useState("Master Data Management");

	const [currentPage, setCurrentPage] = useState(1);
	const [pageInputValue, setPageInputValue] = useState("1");
	const [loading, setLoading] = useState(false);

	const [displayedSearchText, setDisplayedSearchText] = useState("");
	const [searchTimeout, setSearchTimeout] = useState(null);
	const [searchText, setSearchText] = useState("");
	const [filter, setFilter] = useState({});

	const pageLimit = 10;
	const { goTo } = useNavigate();

	const { breadcrumbsItems } = usePage({
		title: pageTitle,
		breadcrumbs: [
			{
				label: "",
				link: `/`,
				isHomePage: isHomePage,
			},
			{
				label: "Master Data Management",
				link: `/master-data-management`,
			},
		],
	});

	/*=================================================
		Error Modal
	/================================================*/
	const { toastList, addToastMessage } = useToast();
	const [error, setError] = useState();

	useEffect(() => {
		addToastMessage(error, toastType.error);
	}, [error]);

	/*=================================================
		Master Data Management
	/================================================*/
	const {
		fetchMasterDataUpload,
		masterDataList,
		metaData,
		masterDataLoading,
	} = useMasterDataManagement();

	/*=================================================
		Sort
	/================================================*/
	const { sortConfig, handleSort } = useDataPage({
		initialSortConfig: { key: "name", direction: true },
	});

	function getClassName(name) {
		if (!sortConfig) {
			return;
		}
		return sortConfig.key === name ? sortConfig.direction === true ? "is-asc" : "is-desc" : "";
	}

	/*=================================================
		Pagination Events
	/================================================*/
	function goToPrev() {
		if (currentPage === 1) return;
		const newPage = currentPage - 1;
		setCurrentPage(newPage);
		setPageInputValue(newPage.toString());
	}

	function goToNext() {
		if (currentPage === metaData?.totalPages) return;
		const newPage = currentPage + 1;
		setCurrentPage(newPage);
		setPageInputValue(newPage.toString());
	}

	function handlePageInputChange(e) {
		const inputValue = e.target.value;
		setPageInputValue(inputValue);
		
		// Only update page for valid numbers within range
		if (inputValue !== "") {
			const pageNumber = Number(inputValue);
			const totalPages = metaData?.totalPages || 1;
			if (pageNumber >= 1 && pageNumber <= totalPages) {
				setCurrentPage(pageNumber);
			}
		}
	}

	function handlePageInputBlur() {
		const pageNumber = Number(pageInputValue);
		const totalPages = metaData?.totalPages || 1;
		
		if (pageInputValue === "" || pageNumber < 1) {
			// Empty or invalid, go to page 1
			setCurrentPage(1);
			setPageInputValue("1");
		} else if (pageNumber > totalPages) {
			// More than total pages, go to last page
			setCurrentPage(totalPages);
			setPageInputValue(totalPages.toString());
		}
	}

	function handlePageInputKeyDown(e) {
		if (e.key === 'Enter') {
			e.target.blur();
		}
	}

	/*=================================================
		Search
	/================================================*/
	const handleSearch = (value) => {
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}
		setSearchTimeout(
			setTimeout(() => {
				setCurrentPage(1);
				setSearchText(value);
				setFilter({
					search: value,
				});
			}, 500)
		);
	};

	/*=================================================
		Refetch Data Filters
	/================================================*/
	const refetchData = (page = currentPage) => {
		const params = {
			page: page,
			limit: 10,
			sortOrder: sortConfig.direction === true ? "ASC" : "DESC",
			sortBy: sortConfig.key,
			searchBy: searchText,
		};
		setCurrentPage(page);
		fetchMasterDataUpload(params);
	};

	useEffect(() => {
		refetchData();
		setPageInputValue(currentPage.toString());
	}, [currentPage, filter, sortConfig]);

	/*=================================================
		List of Master Data
	/================================================*/
	const list = masterDataList?.map((item, index) => {
		return (
			<div className="data-table__table__body__row" key={index + 1}>
				<div
					className="data-table__table__body__row-cursor"
					data-testid="view-transactions-details"
					onClick={() => {
						onViewDetailBtnClick(item);
					}}
					role="none"
				>
					<div
						className={`data-table__table__body__content etr-issuance-data-table__table__body__content-master-data-name etr-issuance-data-table__table__body__content-master-data-display`}
						data-testid="name"
					>
						{item?.name ?? "-"}
					</div>
					<div
						className={`data-table__table__body__content etr-issuance-data-table__table__body__content-master-data-description etr-issuance-data-table__table__body__content-master-data-display`}
						data-testid="description"
					>
						<div>{item?.description ?? "-"}</div>
					</div>
					<div
						className={`data-table__table__body__content etr-issuance-data-table__table__body__content-master-data-name etr-issuance-data-table__table__body__content-master-data-display master-data-upload-management__text-color`}
						data-testid="category"
					>
						<div>{item?.category ?? "-"}</div>
					</div>
					<div
						className="data-table__table__body__content etr-issuance-data-table__table__body__content-master-data-name"
						data-testid="updated-date"
					>
						<div className="master-data-upload-management__text-margin">{`${moment(item.modified).format("DD MMMM YYYY")} ${moment(item.modified).format("h.mmA")} `}</div>
						<div className="master-data-upload-management__text-color">{item.modifiedBy}</div>
					</div>
				</div>
				{isSuperAdmin && (
					<div
						className={`etr-issuance-data-table__table__body__content-action-update`}
						data-testid={`${item.id}-update-actions`}
						onClick={(e) => {
							e.stopPropagation();
							onViewUpdateBtnClick(item.id);
						}}
						role="none"
					>
						<div>
							<IconUploadButton />
						</div>
						<div>{"Update Data"}</div>
					</div>
				)}
				<div
					className={`etr-issuance-data-table__table__body__content-action`}
					data-testid={`${item.id}-actions`}
				>
					<MultiLevelDropDown
						menuList={[
							{
								label: "Download Master Data",
								onClick: (e) => menuItemsClickFunction(e, "download", item.id),
							},
							{
								label: "Download CSV Template",
								onClick: (e) => menuItemsClickFunction(e, "get-template", item.id),
							},
						]}
					/>
				</div>
			</div>
		);
	});

	/*=================================================
		View Master Data Details Navigation
	/================================================*/
	function onViewDetailBtnClick(item) {
		return goTo(`/master-data-management/details/${item.id}`, { item: item, schema: item?.jsonSchema, entityId: item.id });
	}

	function onViewUpdateBtnClick(id) {
		const item = masterDataList?.find(item => item.id === id);
		return goTo(`/master-data-management/update/${id}`, { dataElementName: item?.name, schema: item?.jsonSchema, entityId: item?.id, category: item?.category, uniqueKeysOfSchema: item?.uniqueKeysOfSchema });
	}

	/*=================================================
		Download CSV Template and Master Data
	/================================================*/
	function menuItemsClickFunction(e, type, id) {
		e.stopPropagation();
		if (type === "download") {
			downloadCSVTemplateMasterData(id, "download");
		} else if (type === "get-template") {
			downloadCSVTemplateMasterData(id, "get-template");
		}
	}

	async function downloadCSVTemplateMasterData(id, type) {
		setLoading(true);
		const [error, result] = await api.downloadCSVTemplateMasterData(id, type);
		setLoading(false);
		if (error) {
			return setError(
				error?.error?.message || "Failed to download file(s)"
			);
		}
		const link = document.createElement("a");
		const href = `data:text/csv;charset=utf-8,%EF%BB%BF${encodeURIComponent(
			result?.response
		)}`;
		link.href = href;
		link.download = `${result?.filename ?? type + ".csv"}`;
		link.click();
	}

	const renderPaginationInput = () => (
		<input
			className={"etr-maker-checker__data-table__table__pagination__page"}
			type="number"
			value={pageInputValue}
			onChange={handlePageInputChange}
			onKeyDown={handlePageInputKeyDown}
			onBlur={handlePageInputBlur}
			min="1"
		/>
	);

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />
			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title">{pageTitle}</div>
				</div>
			</div>

			<div className="etr-issuance-record-list-flex">
				<div className="data-table__table__head__row__search">
					<div
						style={{ maxWidth: rem(550), paddingBottom: "1.5rem" }}
					>
						<div style={{ width: "350px" }}>
							<Search
								value={displayedSearchText}
								placeholder={"Search"}
								onChange={(e) => {
									setDisplayedSearchText(e.target.value);
									handleSearch(e.target.value);
								}}
								onCancel={() => {
									setDisplayedSearchText("");
									handleSearch("");
								}}
							/>
						</div>
					</div>
				</div>
			</div>
			<div className="etr-issuance-record-data">
				<div>
					<div className="data-table__table__body">
						<div>
							<div className="data-table__table__head__row">
								<div className="data-table__table__body__row-cursor">
									<div
										className={`data-table__table__head__title data-table__table__head__title__sort-order etr-issuance-data-table__table__head__title-master-data-name link ${getClassName(
											"name"
										)}`}
										onClick={() =>
											handleSort("name")
										}
										data-testid="name-header"
										id="name"
										role="none"
									>
										<div>Name</div>
										{
											<div className="etr-maker-checker-btn-group">
												<button className="data-table__table__head__icon__sort-order-up">
													<IconChevronDown />
												</button>
												<button className="data-table__table__head__icon__sort-order-down">
													<IconChevronDown />
												</button>
											</div>
										}
									</div>
									<div
										className={`data-table__table__head__title data-table__table__head__title__sort-order etr-issuance-data-table__table__head__title-master-data-description link ${getClassName(
											"description"
										)}`}
										onClick={() =>
											handleSort("description")
										}
										data-testid="description-header"
										id="description"
										role="none"
									>
										<div>Description</div>
										<div className="etr-maker-checker-btn-group">
											<button className="data-table__table__head__icon__sort-order-up">
												<IconChevronDown />
											</button>
											<button className="data-table__table__head__icon__sort-order-down">
												<IconChevronDown />
											</button>
										</div>
									</div>
									<div
										className={`data-table__table__head__title data-table__table__head__title__sort-order etr-issuance-data-table__table__head__title-master-data-name link ${getClassName(
											"category"
										)}`}
										onClick={() => handleSort("category")}
										data-testid="category-header"
										id="category"
										role="none"
									>
										<div>Category</div>
										<div className="etr-maker-checker-btn-group">
											<button className="data-table__table__head__icon__sort-order-up">
												<IconChevronDown />
											</button>
											<button className="data-table__table__head__icon__sort-order-down">
												<IconChevronDown />
											</button>
										</div>
									</div>
									<div
										className={`data-table__table__head__title data-table__table__head__title__sort-order etr-issuance-data-table__table__head__title-master-data-name link ${getClassName(
											"lastUpdate"
										)}`}
										id="lastUpdate"
										role="none"
									>
										<div>Last Update</div>
									</div>
								</div>
								{isSuperAdmin && (
									<div
										className={`data-table__table__head__title__sort-order etr-issuance-data-table__table__head__title-action-update link`}
									></div>
								)}
								<div
									className={`data-table__table__head__title__sort-order etr-issuance-data-table__table__head__title-action link`}
								></div>
							</div>
							{masterDataList?.length > 0 ? (
								list
							) : (
								<div className="etr-issuance-no-message-record">
									<div className="data-table__empty__icon">
										<IconNoMessage />
										<div>No Master Data Uploaded yet.</div>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
				{masterDataList?.length > 0 && (
					<div className="data-table__table__body-footer">
						<div className="data-table__table__footer">
							<div className="data-table__table__footer__row">
								<div className="data-table__table__footer__row__left">
									<div className="data-table__table__meta">
										Showing{" "}
										{getPageLabel(
											metaData?.total,
											pageLimit,
											currentPage - 1
										)}{" "}
										of {metaData?.total || 0} entries
									</div>
								</div>
								<div className="data-table__table__footer__row__right">
									<div className="data-table__table__pagination">
										{renderPaginationInput()}
										<span className="data-table__table__pagination__page__meta">
											{" "}
											of {metaData?.totalPages ||
												1} pages{" "}
										</span>
										<button
											data-testid="pagination-prev"
											className={
												"etr-maker-checker__data-table__table__pagination__prev"
											}
											onClick={() => goToPrev()}
										>
											<IconChevronDown />
										</button>
										<button
											data-testid="pagination-next"
											className={
												"etr-maker-checker__data-table__table__pagination__next"
											}
											onClick={() => goToNext()}
										>
											<IconChevronDown />
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}
			</div>

			{(masterDataLoading || loading) &&
				ReactDOM.createPortal(
					<div className="modal white">
						<div className="loading">
							<img src="/loading.gif" alt="loader" />
						</div>
					</div>,
					document.body
				)}

			<Toast autoDelete={true} toastList={toastList} />
		</>
	);
};

export default MasterDataManagementPage;
